import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables';
import { AUFTRAG_API } from '@Constants/backendConstants';
import { initialOrderSorting } from '@Constants/TableColumns';
import { useLoadStagingObjects } from '@Hooks/Staging';
import { type IHasTableModes } from '@Hooks/useTableModes';
import { type ApiOrder, EnumFrontendLayoutSetting, EnumStagingStatus } from '@Model';
import {
    Badge,
    Button,
    defaultIconSize,
    FloatingToolbar,
    InputPromptModal,
    isNullOrEmpty,
    SingleSelect,
    useModalState,
    useOnTheFlyModal,
    useTableRefetch,
    type ToolbarOptions,
} from '@synqo/frontend-component-library';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { type FC, useCallback, useState } from 'react';
import { FrameEditOrder, FrameEditOrderProps } from '../Editing';
import { type UseOrderTableModes } from './useOrderTableModes';
import { useUpdateStagingStatus } from '@Hooks/Staging/useUpdateStagingStatus';
import { useStagedOrderColumns } from '../../../columns';
import { useListApi } from '@Hooks/useListApi';
import { useOpenFromQuery } from '@Hooks/useOpenFromQuery';
import { useAuftragRepository } from '@Hooks/useRepository';
import { OrdersRepository } from '@Repositories/Auftragsbearbeitung';

interface FrameListStagedOrderProps extends IHasTableModes<UseOrderTableModes> {}

/**
 * Displays the list of staged ApiOrder imports.
 * Along with the order, additional metadata about the import is shown.
 **/
export const FrameListStagedOrders: FC<FrameListStagedOrderProps> = ({
    tableModeValue,
    setTableMode,
    availableTableModes,
}) => {
    const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
    const { refetchRef, refetchTable } = useTableRefetch();
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();
    const { isOpen: isRejectionModalOpen, open: openRejectionModal, close: closeRejectionModal } = useModalState();

    const { loadPage } = useListApi(OrdersRepository, AUFTRAG_API, ['all']);
    const orders = useAuftragRepository(OrdersRepository);

    const loadStagedOrders = useLoadStagingObjects(loadPage);
    const { importObjects, rejectObjects, markAsSeen } = useUpdateStagingStatus(`${AUFTRAG_API}/orders`, refetchTable);

    const openOrderPreviewModal = useCallback(
        (apiOrder: ApiOrder) => {
            if (apiOrder.staging_status === undefined || apiOrder.staging_status === EnumStagingStatus.New) {
                markAsSeen([apiOrder.id]);
            }

            let modalId = '';
            const frameProps: FrameEditOrderProps = {
                primaryKey: apiOrder.id,
                canCreate: false,
                canDelete: false,
                events: {
                    onClose: () => closeOnTheFlyModals(modalId),
                    onBack: undefined,
                },
                embedded: true,
                staging: true,
            };
            modalId = openOnTheFlyModal({
                child: <FrameEditOrder {...frameProps} />,
                onClose: refetchTable,
            });
            return true;
        },
        [markAsSeen, openOnTheFlyModal, closeOnTheFlyModals, refetchTable],
    );

    const loadOrder = useCallback((id: string) => orders.get(id, ''), [orders]);
    useOpenFromQuery(loadOrder, openOrderPreviewModal);

    const onImportOrders = useCallback(async () => {
        importObjects(selectedOrderIds);
        setSelectedOrderIds([]);
    }, [selectedOrderIds, setSelectedOrderIds, importObjects]);

    const onConfirmRejectImport = useCallback(
        async (reason: unknown) => {
            if (typeof reason !== 'string' || isNullOrEmpty(reason)) {
                return;
            }

            setSelectedOrderIds([]);
            await rejectObjects(selectedOrderIds, reason);
            closeRejectionModal();
        },
        [selectedOrderIds, setSelectedOrderIds, rejectObjects, closeRejectionModal],
    );

    const columns = useStagedOrderColumns({ onLinked: refetchTable });

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            availableTableModes.length > 1 && (
                <SingleSelect<UseOrderTableModes>
                    key="tableMode"
                    name="tableMode"
                    items={availableTableModes}
                    value={tableModeValue}
                    onChange={setTableMode}
                />
            ),
        ],
        [availableTableModes, tableModeValue, setTableMode],
    );

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings<ApiOrder>
                layoutSettingsContext={EnumFrontendLayoutSetting.OrdersTableLayout}
                name="StagedOrderList"
                columns={columns}
                loader={loadStagedOrders}
                canCreate={false}
                initialSorting={initialOrderSorting}
                onSelect={openOrderPreviewModal}
                objectSelector={{
                    selectedObjectIds: selectedOrderIds,
                    setSelectedObjectIds: setSelectedOrderIds,
                }}
                toolbarElements={toolbarElements}
                fullSize
                refetchRef={refetchRef}
            />
            <FloatingToolbar isOpen={selectedOrderIds.length > 0}>
                <Badge size="sm" type="input" variant="primary">
                    {selectedOrderIds.length.toString()}
                </Badge>
                Aufträge ausgewählt
                <Button appearance="filled" variant="success" onClick={onImportOrders}>
                    <IconCircleCheck size={defaultIconSize} />
                    &nbsp;Importieren
                </Button>
                <Button appearance="filled" variant="warning" onClick={openRejectionModal}>
                    <IconCircleX size={defaultIconSize} />
                    &nbsp;Ablehnen
                </Button>
            </FloatingToolbar>
            <InputPromptModal
                name="ImportRejectedModal"
                value=""
                label="Grund der Ablehnung"
                title={'Import zurückweisen'}
                message={'Bitte geben Sie einen Grund ein, weshalb der Import der Aufträge abgelehnt wurde.'}
                isOpen={isRejectionModalOpen}
                confirm={{
                    variant: 'success',
                    onClick: onConfirmRejectImport,
                }}
                onClose={closeRejectionModal}
                required
            />
        </>
    );
};
